import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import { impersonationAtom } from "~/atoms/impersonation";
import {
  sandboxHealthAtom,
  sandboxOverrideUrlAtom,
} from "~/atoms/sandboxOverride";
import { getCurrentEnv } from "~/common/utils";
import SandboxHealthChip from "./SandboxHealthChip";

const HEALTH_POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Bottom-center pill showing the active sandbox-server override + a live
 * /health chip. Mirrors ImpersonationBanner.tsx layout but blue.
 *
 * Renders only when an override is set AND env is dev. Mounting the banner
 * also drives the /health poller via useEffect — gated identically — so
 * polling is naturally scoped to a dev session with an active override.
 *
 * Stacks above ImpersonationBanner (bottom-4 → bottom-16) when both are
 * visible, so the two pills don't overlap.
 */
const SandboxOverrideBanner = () => {
  const [override, setOverride] = useAtom(sandboxOverrideUrlAtom);
  const [health, setHealth] = useAtom(sandboxHealthAtom);
  const impersonation = useAtomValue(impersonationAtom);

  useEffect(() => {
    if (!override || getCurrentEnv() !== "dev") return;
    const controller = new AbortController();

    const probe = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const res = await fetch(`${override}/health`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setHealth({
          status: res.ok ? "up" : "down",
          lastCheckedAt: Date.now(),
        });
      } catch {
        // Aborts (unmount / override change) skip the state update.
        // Anything else (TypeError, DNS, CORS) is treated as down.
        if (controller.signal.aborted) return;
        setHealth({ status: "down", lastCheckedAt: Date.now() });
      }
    };

    void probe();
    const interval = window.setInterval(probe, HEALTH_POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void probe();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [override, setHealth]);

  if (!override || getCurrentEnv() !== "dev") return null;

  const handleClear = () => {
    setOverride(null);
    setHealth({ status: "unknown", lastCheckedAt: null });
    window.location.reload();
  };

  // Push to bottom-16 (64px) when impersonation is also active so the
  // sandbox pill clears the impersonation pill (which sits at bottom-4
  // with ~36px height) instead of overlapping it.
  const bottomClass = impersonation.isActive ? "bottom-16" : "bottom-4";

  return (
    <div
      className={`fixed ${bottomClass} left-1/2 -translate-x-1/2 bg-sky-500 text-indigo-text-on-accent
        rounded-full px-4 py-2 flex items-center gap-3 shadow-lg`}
      style={{ zIndex: "var(--indigo-z-custom-toast)" }}
    >
      <SandboxHealthChip status={health.status} />
      <span className="text-xs font-medium whitespace-nowrap">
        Sandbox: <strong>{override}</strong>
      </span>
      <button
        onClick={handleClear}
        className="bg-indigo-canvas-highlight text-sky-700 px-3 py-0.5 rounded-full text-xs font-medium
          hover:bg-sky-50 transition-colors whitespace-nowrap"
      >
        Clear
      </button>
    </div>
  );
};

export default SandboxOverrideBanner;
